<template>
    <div class="modal-card card">
        <div class="card-header">
            <!-- Title -->
            <h4 class="card-header-title">Job Config Validation - <code>{{ job.name }}</code></h4>
            <!-- Close -->
            <div v-if="editMode">
                <button class="btn btn-success m-2" :disabled="isButtonDisabled || saving" @click="onSaveClick(false)">
                    <i v-if="isButtonDisabled" class="fa fa-circle-exclamation" title="Please fix lint errors"
                        data-bs-toggle="tooltip" data-bs-placement="left"></i>
                    <div v-if="saving" class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <div v-else>
                        Save
                    </div>
                </button>
                <button class="btn btn-secondary m-2" @click="onCancelClick()">
                    Cancel
                </button>
            </div>
            <div v-else>
                <button class="btn btn-primary m-2" @click="onEditClick()">
                    Edit
                </button>
            </div>
            <button type="button" class="btn-close ms-3" data-bs-dismiss="modal" aria-label="Close"
                @click="readJobTrigger"></button>
        </div>
        <div class="card-body">
            <ConfigValidation v-on:onLintFailure="onLintFailure" :editMode="editMode"
                :jobType="job.kind" :content="yamlContent" :errors="errors" :activeTab="activeTab" :validating="validating" />
            </div>
        <div class="card-footer justify-content-end d-flex align-items-center gap-3">
            <button class="btn btn-primary" :disabled="validating || editMode" @click="onValidateClick()">
                <div v-if="validating" class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <div v-else>
                    Validate
                </div>
            </button>
        </div>
    </div>
</template>

<script>
import ConfigValidation from '../../components/ConfigValidation.vue';
import { mapActions } from "vuex";
export default {
    components: { ConfigValidation },
    data() {
        return {
            snapshot: null,
            job: this.modalData.job,
            errors: this.modalData.validationErrors,
            jobObj: null,
            yamlContent: null,
            activeTab: null,
            editMode: false,
            saving: false,
            validating: false,
            isButtonDisabled: false,
        };
    },
    props: {
        modalData: {
            type: [Object, Array, String],
        },
    },
    name: "JobConfigValidation",
    methods: {
        ...mapActions({
            readSandboxJob: "sandbox/readSandboxJob",
            updateSandboxJob: "sandbox/updateSandboxJob",
            validateJobConfig: "sandbox/validateJobConfig",
            showModal: "showModal",
        }),
        onLintFailure(isError) {
            this.isButtonDisabled = isError
        },
        readJobTrigger() {
            this.$store.dispatch('sandbox/updateReadJobTrigger')
        },
        onReloadChanges() {
            this.$store.dispatch('hideModal');
            this.readJobTrigger();
        },
        onOverwriteChanges() {
            this.onSaveClick(true);
            this.$store.dispatch('hideModal');
            this.readJobTrigger();
        },
        initateYamlContent() {
            this.jobObj = this.job
            let yamlContent = {};

            if (this.job.system_files) {
                for (const i of this.job.system_files) {
                    yamlContent[" " + i.relative_path] = i.content
                }
            }
            if (this.job.user_files) {
                for (const i of this.job.user_files) {
                    yamlContent[" " + i.relative_path] = i.content
                }
            }
            //Initially activeTab value is empty or undefined otherwise no need to reset to first value
            if (!this.activeTab || this.activeTab == "") {
                this.activeTab = Object.keys(yamlContent)[0];
            }
            this.yamlContent = JSON.parse(JSON.stringify(yamlContent));
        },
        async checkConfigChanged(skipCheck) {
            let response;
            let self = this;
            try {
                if (skipCheck) return true;
                response = await this.readSandboxJob(this.$route.params.jobId);
                let dbJob = response.data;
                if (dbJob.updated_at !== this.snapshot.updated_at) {
                    let diff = [];
                    ["system_files", "user_files"].map((key) => {
                        //system_files or userfiles exits in server config or local snapshot
                        if (dbJob[key] || self.snapshot[key]) {
                            //if system_files or user_file doesn't exits in snapshot set empty array
                            if (!self.snapshot[key])
                                self.snapshot[key] = []
                            //if server config contain userfiles compare with snapshot and add to difference
                            if (dbJob[key]) {
                                dbJob[key].map((file) => {
                                    let oldConfig = self.snapshot[key].find((snapfile) => snapfile.relative_path === file.relative_path);
                                    //if snapshot doesn't contain the file then set empty files showing new file added
                                    if (!oldConfig)
                                        oldConfig = { relative_path: file.relative_path, content: '' }
                                    diff.push({ current: file, prev: oldConfig })
                                })
                            } else {
                                //if snapshot contail file but server config doesn't means deleted the user files or system_files
                                self.snapshot[key].map((config) => {
                                    diff.push({ current: { relative_path: config.relative_path, content: '' }, prev: config })
                                })
                            }
                        }
                    })
                    this.dbJobSnapshot = dbJob;
                    this.showModal({ component: "ConfigChanges", data: { diff: diff, details: dbJob, onReload: this.onReloadChanges, onOverWrite: this.onOverwriteChanges } })
                    return false
                }
                return true
            } catch (error) {
                let err = (error.response.data.error) ? err = error.response.data.error : err = error.message;
                console.error(err);
                return false
            }
        },
        async onSaveClick(skipCheck) {
            this.saving = true;
            let toastOptions;
            let noConflict = await this.checkConfigChanged(skipCheck) // check for conflicts before update
            if (noConflict) {
                try {
                    let systemFileKeys = [];
                    let userFileKeys = [];

                    for (const i of Object.keys(this.yamlContent)) {
                        let isSystemFile = false;
                        if (this.job.system_files) {
                            for (const j of this.job.system_files) {
                                if (j.relative_path == i.slice(1)) isSystemFile = true;
                            }
                        }
                        if (i.toLowerCase().slice(1) == "vars.yaml") isSystemFile = true;
                        isSystemFile ? systemFileKeys.push(i.slice(1)) : userFileKeys.push(i.slice(1));
                    }

                    this.jobObj.system_files = systemFileKeys.map((key) => {
                        return { content: (typeof this.yamlContent[" " + key] === 'string') ? this.yamlContent[" " + key] : JSON.stringify(this.yamlContent[" " + key]), relative_path: key };
                    });
                    this.jobObj.user_files = userFileKeys.map((key) => {
                        return { content: (typeof this.yamlContent[" " + key] === 'string') ? this.yamlContent[" " + key] : JSON.stringify(this.yamlContent[" " + key]), relative_path: key };
                    });

                    const response = await this.updateSandboxJob(this.jobObj);
                    toastOptions = {
                        message: "Job updated successfully.",
                        type: "success"
                    }
                } catch (error) {
                    let err = error.response && error.response.data && error.response.data.error ? error.response.data.error : error.message;
                    toastOptions = {
                        message: err || "Something went wrong when updating Job. Please contact support.",
                        type: "error"
                    }
                } finally {
                    this.saving = false;
                    this.$store.dispatch('addToast', toastOptions);
                }

                // read sandbox job after update
                await this.readSandboxJob(this.$route.params.jobId).then(response => {
                    this.job = response.data;
                    this.snapshot = JSON.parse(JSON.stringify(this.job)); // deep clone
                    this.initateYamlContent();
                }).catch(err => {
                    let error = err.response ? err.response.data.error : err.message;
                    toastOptions = {
                        message: error || "Something went wrong while fetching job. Please contact support.",
                        type: "error"
                    }
                    this.$store.dispatch('addToast', toastOptions);
                })
                this.editMode = false;
            }
        },
        onCancelClick() {
            this.editMode = false;
            this.job = { ...this.snapshot };
            this.initateYamlContent();
        },
        onEditClick() {
            this.editMode = true;
        },
        async onValidateClick() {
            this.validating = true;
            this.error = null;
            let toastOptions;
            const configFile = this.job.system_files.find((file) => file.relative_path === "config.yaml");
            const varsFile = this.job.system_files.find((file) => file.relative_path === "vars.yaml");
            let payload = {
                job_kind: this.job.kind,
                config: configFile.content
            }
            if (varsFile?.content) {
                payload["vars"] = varsFile.content;
            }
            await this.validateJobConfig(
                payload
            ).then(response => {
                if (response.data?.validation_errors && response.data.validation_errors.length != 0) {
                    this.errors = response.data.validation_errors;
                } else {
                    toastOptions = {
                        message: "Job config validated successfully.",
                        type: "success"
                    }
                    this.$store.dispatch('hideModal');
                    this.readJobTrigger();
                }
            }).catch(err => {
                let error = err.response ? err.response.data.error : err.message;
                toastOptions = {
                    message: error || "Something went wrong while validating job configuration. Please contact support.",
                    type: "error"
                }
                this.$store.dispatch('hideModal');
                this.readJobTrigger();
            })
            if (toastOptions) this.$store.dispatch('addToast', toastOptions);
            this.validating = false;
        }
    },
    beforeMount() {
        this.snapshot = JSON.parse(JSON.stringify(this.job)); // deep clone
        this.initateYamlContent();
    },
    emits: ['onLintFailure']
};
</script>

<style scoped>
::v-deep(.nav-tabs .nav-link.active) {
    color: #495057 !important;
    background-color: #edf1f7 !important;
    border-color: #9eb5d7 !important;
}

code {
  font-family: monospace;
  background-color: #edf1f7;
  color: #335ed6;
  padding: 2px 6px;
  border-radius: 2px;
  font-size: 100%;
  white-space: nowrap;
}
</style>